import { useMemo, useRef } from "react";
import { toast } from "sonner";
import { X, Printer } from "@phosphor-icons/react";
import { api, formatApiError } from "@/lib/api";
import { useI18n } from "@/context/I18nContext";
import BarcodeLabel from "@/components/BarcodeLabel";

/**
 * LabelPrintModal — preview + print for single/bulk barcode labels.
 * items: [{ sku, qty, style_name, color, size, barcode, barcode_type, retail_price, currency }]
 * template: "sticker_40x25" | "a4_sheet_3x8"
 */
export default function LabelPrintModal({
  open,
  items = [],
  onClose,
  showPrice = true,
  template = "a4_sheet_3x8",
}) {
  const { t } = useI18n();
  const printableRef = useRef(null);

  const expanded = useMemo(() => {
    const out = [];
    for (const it of items) {
      const qty = Math.max(1, Math.min(200, Number(it.qty || 1)));
      for (let i = 0; i < qty; i++) out.push(it);
    }
    return out;
  }, [items]);

  if (!open) return null;

  const doPrint = async () => {
    try {
      await api.post("/barcodes/print", {
        items: items.map((i) => ({ sku: i.sku, qty: Number(i.qty || 1) })),
        template,
      });
    } catch (e) {
      // Do not block printing on audit failure — just warn
      if (e?.response?.status !== 403) toast.error(formatApiError(e));
    }
    window.print();
  };

  return (
    <div
      className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4 print:bg-white print:p-0"
      onClick={onClose}
      data-testid="label-print-modal"
    >
      <div
        className="w-full max-w-4xl bg-white border shadow-xl max-h-[92vh] flex flex-col print:max-h-none print:border-0 print:shadow-none print:w-auto print:max-w-none"
        style={{ borderColor: "var(--nylsu-border-strong)" }}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="p-4 border-b flex items-center justify-between print:hidden" style={{ borderColor: "var(--nylsu-border)" }}>
          <div>
            <div className="overline">{t("barcode.printPreview")}</div>
            <div className="text-xs text-[#8F8D84] mt-1">
              {expanded.length} {t("barcode.labels")} · {template === "a4_sheet_3x8" ? t("barcode.templateA4") : t("barcode.templateSticker")}
            </div>
          </div>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={doPrint}
              className="px-4 py-2 text-[10px] uppercase tracking-[0.15em] border inline-flex items-center gap-2"
              style={{ background: "var(--nylsu-gold)", borderColor: "var(--nylsu-gold)", color: "#0A0A0A" }}
              data-testid="label-print-btn"
            >
              <Printer size={14} weight="light" /> {t("barcode.print")}
            </button>
            <button type="button" onClick={onClose} className="p-2 text-[#8F8D84] hover:text-[#0A0A0A]">
              <X size={16} weight="light" />
            </button>
          </div>
        </div>

        <div className="flex-1 overflow-y-auto p-6 bg-[#F6F5EE] print:bg-white print:p-0" ref={printableRef}>
          <div
            className="mx-auto"
            style={{
              display: "grid",
              gridTemplateColumns: template === "a4_sheet_3x8" ? "repeat(3, 220px)" : "repeat(auto-fill, 220px)",
              gap: template === "a4_sheet_3x8" ? "6px" : "4px",
              justifyContent: "center",
            }}
            data-testid="label-print-grid"
          >
            {expanded.map((it, idx) => (
              <BarcodeLabel
                key={`${it.sku}-${idx}`}
                styleName={it.style_name}
                color={it.color}
                size={it.size}
                sku={it.sku}
                barcode={it.barcode || it.sku}
                price={showPrice ? it.retail_price : undefined}
                currency={it.currency || "EUR"}
                format={it.barcode_type === "ean13" ? "EAN13" : "CODE128"}
              />
            ))}
            {expanded.length === 0 && (
              <div className="text-center text-xs text-[#8F8D84] py-16">
                {t("barcode.noLabels")}
              </div>
            )}
          </div>
        </div>
      </div>

      <style>{`
        @media print {
          body * { visibility: hidden !important; }
          [data-testid="label-print-grid"], [data-testid="label-print-grid"] * { visibility: visible !important; }
          [data-testid="label-print-grid"] { position: absolute; left: 8mm; top: 8mm; }
          @page { size: A4; margin: 8mm; }
        }
      `}</style>
    </div>
  );
}
